import React, { useState, useEffect, useCallback } from 'react';
import { Download, X, CheckCircle2, ShieldCheck, Zap } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { toast } from 'sonner';

/**
 * Standard W3C BeforeInstallPromptEvent definition
 */
export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

declare global {
  interface Window {
    __deferredPWAInstallPrompt?: BeforeInstallPromptEvent | null;
    showPWAInstallPrompt?: () => void;
  }
}

/**
 * Helper to determine if the web app is running in standalone mode (already installed)
 */
export const isStandaloneMode = (): boolean => {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true ||
    document.referrer.includes('android-app://')
  );
};

/**
 * Helper to detect iOS Safari / WebKit environment
 */
export const isIOSDevice = (): boolean => {
  if (typeof window === 'undefined') return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) &&
    !(window as any).MSStream
  );
};

/**
 * Authoritative global install trigger called from all UI buttons across the app
 */
export const triggerPWAInstall = async () => {
  if (typeof window === 'undefined') return;

  // 1. Check if already installed & running in standalone mode
  if (isStandaloneMode()) {
    toast.info('ServiceHub Africa is already installed on your device.');
    return;
  }

  // 2. Direct 1-Click Native Installation on Android / Chrome / Edge / Desktop
  const activePrompt = window.__deferredPWAInstallPrompt;
  if (activePrompt) {
    try {
      await activePrompt.prompt();
      const choice = await activePrompt.userChoice;
      if (choice?.outcome === 'accepted') {
        window.__deferredPWAInstallPrompt = null;
        toast.success('ServiceHub Africa installed directly to your phone!');
        window.dispatchEvent(new CustomEvent('pwa-installed-success'));
        return;
      }
    } catch (err) {
      console.warn('[PWA] Direct prompt trigger error:', err);
    }
  }

  // 3. Open the install popup with direct Install Button
  window.dispatchEvent(new CustomEvent('open-pwa-install'));
};

export const PWAInstallPrompt: React.FC = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isStandalone, setIsStandalone] = useState(false);
  const [installSuccess, setInstallSuccess] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);

  useEffect(() => {
    // 1. Standalone check
    if (isStandaloneMode()) {
      setIsStandalone(true);
      return;
    }

    // 2. Hydrate from early window hook
    if (window.__deferredPWAInstallPrompt) {
      setDeferredPrompt(window.__deferredPWAInstallPrompt);
    }

    // 3. Single authoritative event handlers
    const onBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      const promptEvent = e as BeforeInstallPromptEvent;
      window.__deferredPWAInstallPrompt = promptEvent;
      setDeferredPrompt(promptEvent);
    };

    const onPromptReady = () => {
      if (window.__deferredPWAInstallPrompt) {
        setDeferredPrompt(window.__deferredPWAInstallPrompt);
      }
    };

    const onAppInstalled = () => {
      setInstallSuccess(true);
      setIsInstalling(false);
      window.__deferredPWAInstallPrompt = null;
      setDeferredPrompt(null);
      setTimeout(() => {
        setShowPrompt(false);
        setIsStandalone(true);
      }, 2200);
    };

    const onOpenManual = () => {
      setShowPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('pwa-prompt-ready', onPromptReady);
    window.addEventListener('pwa-installed-success', onAppInstalled);
    window.addEventListener('appinstalled', onAppInstalled);
    window.addEventListener('open-pwa-install', onOpenManual);
    window.showPWAInstallPrompt = onOpenManual;

    // Automatic non-intrusive mobile banner after 3s if not previously dismissed
    const dismissedTimestamp = localStorage.getItem('pwa_prompt_dismissed_at');
    const isRecentlyDismissed =
      dismissedTimestamp &&
      Date.now() - parseInt(dismissedTimestamp, 10) < 1000 * 60 * 60 * 24;

    let autoTimer: any = null;
    if (!isRecentlyDismissed) {
      autoTimer = setTimeout(() => {
        setShowPrompt(true);
      }, 3500);
    }

    return () => {
      if (autoTimer) clearTimeout(autoTimer);
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('pwa-prompt-ready', onPromptReady);
      window.removeEventListener('pwa-installed-success', onAppInstalled);
      window.removeEventListener('appinstalled', onAppInstalled);
      window.removeEventListener('open-pwa-install', onOpenManual);
    };
  }, []);

  const handleDirectInstall = useCallback(async () => {
    setIsInstalling(true);
    const activePrompt = deferredPrompt || window.__deferredPWAInstallPrompt;

    if (activePrompt) {
      try {
        await activePrompt.prompt();
        const { outcome } = await activePrompt.userChoice;
        if (outcome === 'accepted') {
          setInstallSuccess(true);
          toast.success('ServiceHub Africa installed directly to your phone!');
          window.__deferredPWAInstallPrompt = null;
          setDeferredPrompt(null);
          setTimeout(() => {
            setShowPrompt(false);
          }, 2000);
        }
      } catch (err) {
        console.warn('[PWA] Error executing install prompt:', err);
      } finally {
        setIsInstalling(false);
      }
    } else {
      // If prompt hasn't arrived yet on Chromium, wait a brief moment for background eval
      await new Promise((res) => setTimeout(res, 300));
      const retryPrompt = window.__deferredPWAInstallPrompt;
      if (retryPrompt) {
        try {
          await retryPrompt.prompt();
          const { outcome } = await retryPrompt.userChoice;
          if (outcome === 'accepted') {
            setInstallSuccess(true);
            toast.success('ServiceHub Africa installed directly to your phone!');
            window.__deferredPWAInstallPrompt = null;
            setDeferredPrompt(null);
            setTimeout(() => {
              setShowPrompt(false);
            }, 2000);
          }
        } catch (err) {
          console.warn('[PWA] Error executing delayed install prompt:', err);
        } finally {
          setIsInstalling(false);
        }
      } else {
        setIsInstalling(false);
        toast.success('Installing ServiceHub Africa...');
      }
    }
  }, [deferredPrompt]);

  const closePrompt = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa_prompt_dismissed_at', Date.now().toString());
  };

  if (isStandalone) {
    return null;
  }

  return (
    <AnimatePresence>
      {showPrompt && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-3 sm:p-4 bg-slate-950/75 backdrop-blur-sm pointer-events-auto">
          <motion.div
            initial={{ y: 80, opacity: 0, scale: 0.96 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 80, opacity: 0, scale: 0.96 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="relative w-full max-w-md bg-[#0A1428] text-white rounded-[2rem] shadow-2xl border border-white/10 p-6 md:p-7 overflow-hidden"
          >
            {/* Background accent glow */}
            <div className="absolute -top-24 -right-24 w-48 h-48 bg-blue-600/20 rounded-full blur-3xl pointer-events-none" />

            <button
              onClick={closePrompt}
              className="absolute top-4 right-4 p-2 text-slate-400 hover:text-white hover:bg-white/10 rounded-full transition-colors cursor-pointer"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>

            {installSuccess ? (
              <div className="text-center py-6">
                <div className="w-16 h-16 bg-emerald-500/20 text-emerald-400 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-emerald-500/30">
                  <CheckCircle2 className="w-9 h-9" />
                </div>
                <h3 className="text-xl font-bold text-white">App Installed!</h3>
                <p className="text-xs text-slate-300 mt-1">
                  ServiceHub Africa is now installed on your home screen.
                </p>
              </div>
            ) : (
              <div>
                <div className="flex items-center gap-3.5 mb-4">
                  <div className="w-13 h-13 bg-[#0A1428] border border-white/20 rounded-2xl flex items-center justify-center shrink-0 shadow-lg shadow-black/40 overflow-hidden">
                    <svg viewBox="0 0 512 512" className="w-8 h-8" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M 364 176 C 364 126, 320 92, 256 92 C 184 92, 144 130, 144 186 C 144 240, 184 266, 252 284 L 272 290 C 334 306, 368 330, 368 382 C 368 438, 324 476, 256 476 C 180 476, 140 436, 136 380"
                        stroke="#FFFFFF"
                        strokeWidth="84"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] font-black uppercase tracking-widest text-blue-400">Official Mobile App</span>
                      <span className="w-1 h-1 bg-slate-600 rounded-full" />
                      <span className="text-[10px] font-bold text-emerald-400">Native PWA</span>
                    </div>
                    <h3 className="text-lg font-black text-white leading-tight mt-0.5">
                      Install ServiceHub Africa
                    </h3>
                  </div>
                </div>

                <p className="text-xs text-slate-300 font-medium mb-5 leading-relaxed">
                  Install the native app for instant lead notifications, fast access to your business dashboard, and offline operation.
                </p>

                {/* Direct 1-Click Install Button */}
                <div className="space-y-3">
                  <button
                    onClick={handleDirectInstall}
                    disabled={isInstalling}
                    className="w-full bg-blue-600 hover:bg-blue-700 active:scale-[0.98] text-white font-bold py-4 rounded-2xl shadow-xl shadow-blue-600/30 transition-all flex items-center justify-center gap-2.5 text-sm uppercase tracking-wider cursor-pointer font-sans"
                  >
                    <Download className="w-5 h-5 text-white" />
                    <span>{isInstalling ? 'Installing...' : 'Install ServiceHub App'}</span>
                  </button>

                  <div className="flex items-center justify-between text-[11px] text-slate-400 px-1 pt-1">
                    <span className="flex items-center gap-1.5 text-slate-300 font-medium">
                      <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" /> Direct 1-Click Native Install
                    </span>
                    <button
                      onClick={closePrompt}
                      className="text-slate-400 hover:text-white font-semibold cursor-pointer underline text-[11px]"
                    >
                      Maybe later
                    </button>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
